import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCompare } from "./CompareContext";
import { useCatalogData } from "./CatalogDataContext";
import {
  MIXED_MODALITY_COMPARE_MSG,
  isMixedModalityCompare,
} from "../lib/compareModality";
import { CompareSpider } from "./ui/CompareSpider";
import {
  formatNormalized,
  formatUsd,
  isHostAggregator,
  scenarioCost,
} from "../lib/format";
import { ModalityBadge, ConfidenceBadge } from "./Badges";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

type ModalTab = "radar" | "matrix" | "costs";

function CompareModalBody() {
  const { selected, toggle, clear, setModalOpen, openCompare } = useCompare();
  const { models, providers, meta } = useCatalogData();
  const [filterText, setFilterText] = useState("");
  const [intentBusy, setIntentBusy] = useState(false);
  const [intentNote, setIntentNote] = useState<string | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [tab, setTab] = useState<ModalTab>("radar");
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const compared = useMemo(
    () =>
      selected
        .map((id) => models.find((m) => m.id === id))
        .filter((m): m is NonNullable<typeof m> => Boolean(m)),
    [selected, models],
  );

  const mixedModality = isMixedModalityCompare(compared);

  // Intent input is remap-only; never half-filter the overlay set while typing.
  const visible = compared;

  useEffect(() => {
    // Focus input shortly after remount / open.
    const t = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setModalOpen]);

  // Lightweight focus trap inside the card.
  useEffect(() => {
    const root = cardRef.current;
    if (!root) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root.addEventListener("keydown", onTab);
    return () => root.removeEventListener("keydown", onTab);
  }, []);

  async function runScopedIntent(query: string) {
    const q = query.trim();
    if (!q || selected.length < 2) return;
    setIntentBusy(true);
    setIntentNote(null);
    setIntentError(null);
    try {
      const res = await fetch("/api/intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q, scopedModelIds: selected }),
      });
      const data = (await res.json()) as {
        compareModelIds?: string[];
        explanation?: string;
        error?: string;
      };
      if (!res.ok) {
        setIntentError(data.error ?? "Intent parse failed");
        return;
      }
      if (data.error) setIntentError(data.error);
      if (data.compareModelIds && data.compareModelIds.length >= 2) {
        // openCompare bumps remapKey → this body remounts cleanly.
        openCompare(data.compareModelIds);
        return;
      }
      setIntentNote(data.explanation ?? "No new compare set from intent");
    } catch {
      setIntentError("Intent unavailable");
    } finally {
      setIntentBusy(false);
    }
  }

  return (
    <div
      ref={cardRef}
      className="modal-card compare-modal-card"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-head compare-modal-head">
        <div className="compare-modal-head-title">
          <span className="compare-modal-count">
            {compared.length} model{compared.length === 1 ? "" : "s"}
          </span>
        </div>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => setModalOpen(false)}
          aria-label="Close"
        >
          Close
        </button>
      </div>

      <div className="compare-modal-toolbar">
        <input
          ref={inputRef}
          className="intent-input"
          style={{ flex: 1, minWidth: 0 }}
          placeholder="Remap selection · Enter to apply"
          value={filterText}
          onChange={(e) => {
            setFilterText(e.target.value);
            setIntentError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void runScopedIntent(filterText);
            }
          }}
          disabled={intentBusy}
          aria-label="Remap compare selection"
        />
        <button
          type="button"
          className="btn sm"
          disabled={intentBusy || !filterText.trim() || selected.length < 2}
          onClick={() => void runScopedIntent(filterText)}
        >
          {intentBusy ? "…" : "Apply"}
        </button>
        <button
          type="button"
          className="compare-modal-link"
          onClick={() => {
            clear();
            setModalOpen(false);
          }}
        >
          Clear
        </button>
        <Link
          className="compare-modal-link"
          to={`/compare?ids=${encodeURIComponent(selected.join(","))}`}
          onClick={() => setModalOpen(false)}
        >
          Full page
        </Link>
      </div>

      {intentBusy ? (
        <p className="compare-modal-note muted" role="status">
          Remapping selection…
        </p>
      ) : null}
      {intentError ? (
        <p className="compare-modal-note compare-modal-error" role="alert">
          {intentError}
        </p>
      ) : null}
      {!intentBusy && !intentError && intentNote ? (
        <p className="compare-modal-note muted">{intentNote}</p>
      ) : null}

      {mixedModality ? (
        <div className="compare-modal-empty">
          <p className="compare-modal-error" role="alert" style={{ margin: 0 }}>
            {MIXED_MODALITY_COMPARE_MSG}
          </p>
          <button
            type="button"
            className="btn sm"
            style={{ marginTop: "0.85rem" }}
            onClick={() => setModalOpen(false)}
          >
            Close
          </button>
        </div>
      ) : compared.length < 2 ? (
        <div className="compare-modal-empty">
          <p className="muted" style={{ margin: 0 }}>
            Select 2–4 models from the catalog, or ask to compare them in
            intent.
          </p>
        </div>
      ) : (
        <div className="compare-modal-body">
          <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as ModalTab)}
              className="compare-modal-tabs"
            >
              <TabsList className="compare-modal-tabs-list">
                <TabsTrigger value="radar">Radar</TabsTrigger>
                <TabsTrigger value="matrix">Matrix</TabsTrigger>
                <TabsTrigger value="costs">Costs</TabsTrigger>
              </TabsList>

              <TabsContent value="radar" className="compare-modal-tab-panel">
                <CompareSpider models={visible} compact panel="radar" />
              </TabsContent>

              <TabsContent
                value="matrix"
                className="compare-modal-tab-panel compare-modal-tab-scroll"
              >
                <CompareSpider models={visible} compact panel="matrix" />
              </TabsContent>

              <TabsContent
                value="costs"
                className="compare-modal-tab-panel compare-modal-tab-scroll"
              >
                <div className="compare-grid" id="compare-cost-grid">
                  {visible.map((m) => {
                    const p = providers.find((x) => x.id === m.providerId);
                    const host = isHostAggregator(m.providerId);
                    const img = scenarioCost(m, "1_image");
                    const vid = scenarioCost(m, "5s_video");
                    return (
                      <div className="compare-col" key={m.id}>
                        <h3>
                          <Link
                            to={`/models/${m.id}`}
                            onClick={() => setModalOpen(false)}
                          >
                            {m.name}
                          </Link>
                        </h3>
                        <div className="compare-badges">
                          {host ? (
                            <span className="pill host">Host</span>
                          ) : (
                            <span className="pill direct">Direct</span>
                          )}
                          <ModalityBadge value={m.modality} />
                        </div>
                        <div className="compare-price">
                          {formatNormalized(m)}
                        </div>
                        <div className="compare-row">
                          <span className="label">Provider</span>
                          <span>{p?.name ?? m.providerId}</span>
                        </div>
                        <div className="compare-row">
                          <span className="label">1 image</span>
                          <span className="mono">
                            {img != null ? formatUsd(img) : "—"}
                          </span>
                        </div>
                        <div className="compare-row">
                          <span className="label">5s video</span>
                          <span className="mono">
                            {vid != null ? formatUsd(vid) : "—"}
                          </span>
                        </div>
                        <div className="compare-row">
                          <span className="label">Confidence</span>
                          <ConfidenceBadge value={m.pricing.confidence} />
                        </div>
                        <button
                          type="button"
                          className="btn ghost sm"
                          style={{ marginTop: "0.5rem" }}
                          onClick={() => toggle(m.id)}
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
                {meta.lastPriceScrapeAt ? (
                  <p
                    className="muted"
                    style={{ marginTop: "0.55rem", fontSize: "0.68rem" }}
                  >
                    Prices scraped{" "}
                    {new Date(meta.lastPriceScrapeAt).toLocaleString("en-IN", {
                      timeZone: "Asia/Calcutta",
                    })}{" "}
                    IST
                  </p>
                ) : null}
              </TabsContent>
            </Tabs>
        </div>
      )}
    </div>
  );
}

export function CompareModal() {
  const { modalOpen, setModalOpen, remapKey } = useCompare();

  if (!modalOpen) return null;

  return (
    <div
      className="modal-backdrop compare-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Compare models"
      onClick={() => setModalOpen(false)}
    >
      {/* key=remapKey remounts body on every openCompare / intent remap */}
      <CompareModalBody key={remapKey} />
    </div>
  );
}
